import { useMemo, useState } from 'react';
import { Box, Dialog, DialogTitle, Tab, Tabs } from '@mui/material';

import { ElementTile } from './ElementTile';
import { GiftForm, GiftFormValue } from './GiftForm';
import { JiraIssueLink } from './JiraIssueLink';
import { PlannedGiftPanel } from './PlannedGiftPanel';

import {
    preferMainById,
    useGameElementsIncludingBranches,
} from '../../hooks/gameElements';
import { GiftSchemas, useGiftAdmin } from '../../hooks/useGiftApi';
import { JiraSubtask } from '../../hooks/useJiraApi';

type Gift = GiftSchemas['FullGiftAdminItem'];

type TabValue = 'planned' | 'scheduled';

interface SelectedPublication {
    publicationId: string;
    startDate: string;
    endDate: string | null;
}

interface GiftDetailDialogProps {
    open: boolean;
    onClose: () => void;
    /** The scheduled gift, when one exists. */
    gift?: Gift | null;
    /** The publication whose calendar tile was clicked. */
    selectedPublication?: SelectedPublication | null;
    /** The planned Jira subtask, when one exists. */
    subtask?: JiraSubtask | null;
    /** Which view to show first when both are present. */
    initialTab?: TabValue;
    /** Whether the planned subtask can be scheduled (drives the action button). */
    canSchedule?: boolean;
}

/**
 * Unified click-through dialog for a gift, used by both the Planner and the
 * Scheduler. It adapts to what is linked:
 *
 * - only a scheduled gift  → the full gift edit/delete form
 * - only a planned subtask → the plan view (metadata, edit-plan, schedule)
 * - both                   → tabs to switch between the two views
 */
export const GiftDetailDialog = ({
    open,
    onClose,
    gift,
    selectedPublication,
    subtask,
    initialTab = 'planned',
    canSchedule = false,
}: GiftDetailDialogProps) => (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        {/* keyed to force fresh form state each time the dialog opens */}
        {open && (
            <GiftDetailContent
                key={`${gift?.id ?? subtask?.key ?? 'empty'}:${selectedPublication?.publicationId ?? ''}`}
                onClose={onClose}
                gift={gift ?? null}
                subtask={subtask ?? null}
                initialTab={initialTab}
                canSchedule={canSchedule}
            />
        )}
    </Dialog>
);

const GiftDetailContent = ({
    onClose,
    gift,
    subtask,
    initialTab,
    canSchedule,
}: {
    onClose: () => void;
    gift: Gift | null;
    subtask: JiraSubtask | null;
    initialTab: TabValue;
    canSchedule: boolean;
}) => {
    const gameElementsQuery = useGameElementsIncludingBranches();
    const gameElements = useMemo(
        () => preferMainById(gameElementsQuery.data?.gameElements ?? []),
        [gameElementsQuery.data],
    );

    const { updateGift, publishGift, updatePublication, deletePublication } =
        useGiftAdmin();

    const saveGift = (value: GiftFormValue) =>
        updateGift(value.id, {
            name: value.name,
            contents: value.contents,
            wrapperGameElementId: value.wrapperGameElementId,
            tutorial: value.tutorial ?? false,
            defaultClaimed: value.defaultClaimed ?? false,
            minimumVersion: value.minimumVersion || null,
            jiraIssueKey: value.jiraIssueKey || null,
        });

    const hasBoth = !!gift && !!subtask;
    const [tab, setTab] = useState<TabValue>(
        hasBoth ? initialTab : gift ? 'scheduled' : 'planned',
    );

    const giftTile = gift && (
        <ElementTile
            gameElementIds={[...gift.contents, gift.wrapperGameElementId]}
            tooltip={gift.name}
            ariaLabel={`${gift.name} icon`}
        />
    );

    const scheduledPanel = gift && (
        <>
            {!hasBoth && (
                <DialogTitle
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                >
                    {giftTile}
                    <Box sx={{ minWidth: 0 }}>{gift.name}</Box>
                </DialogTitle>
            )}
            <GiftForm
                gameElements={gameElements}
                defaultValue={gift}
                publications={gift.publications}
                onSave={async (value) => {
                    await saveGift(value);
                    onClose();
                }}
                onCreatePublication={(window) => publishGift(gift.id, window)}
                onUpdatePublication={(publicationId, window) =>
                    updatePublication(gift.id, publicationId, window)
                }
                onDeletePublication={(publicationId) =>
                    deletePublication(gift.id, publicationId)
                }
                onCancel={onClose}
            />
        </>
    );

    const plannedPanel = subtask && (
        <PlannedGiftPanel
            subtask={subtask}
            onClose={onClose}
            scheduled={!!gift}
            canSchedule={canSchedule}
            showHeader={!hasBoth}
        />
    );

    if (!hasBoth) {
        return <>{gift ? scheduledPanel : plannedPanel}</>;
    }

    return (
        <>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                    pb: 0,
                }}
            >
                {giftTile}
                <Box sx={{ minWidth: 0 }}>{subtask.summary || gift.name}</Box>
                {subtask.url && <JiraIssueLink url={subtask.url} />}
            </DialogTitle>
            <Tabs
                value={tab}
                onChange={(_, next: TabValue) => setTab(next)}
                sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab value="planned" label="Planned" />
                <Tab value="scheduled" label="Scheduled" />
            </Tabs>
            <Box hidden={tab !== 'planned'}>{plannedPanel}</Box>
            <Box hidden={tab !== 'scheduled'}>{scheduledPanel}</Box>
        </>
    );
};
